// Motion helpers (GSAP). Each one is instant under prefers-reduced-motion,
// so components never have to branch on it.
import { gsap } from "gsap";

export { gsap };

export const reducedMotion = (): boolean =>
  import.meta.client && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** Grows an element from 0 to its natural height (open) or folds it back to 0. */
export function animateHeight(
  el: Element,
  open: boolean,
  callbacks: { onUpdate?: () => void; onComplete?: () => void } = {}
) {
  if (reducedMotion()) return void callbacks.onComplete?.();
  gsap.fromTo(
    el,
    { height: open ? 0 : (el as HTMLElement).offsetHeight, autoAlpha: open ? 0 : 1, overflow: "hidden" },
    {
      height: open ? "auto" : 0,
      autoAlpha: open ? 1 : 0,
      duration: open ? 0.3 : 0.28,
      ease: open ? "power2.out" : "power2.inOut",
      overwrite: true,
      clearProps: "height,opacity,visibility,overflow",
      ...callbacks,
    }
  );
}

/** Eases an element from its previous height to the new one after its content changed. */
export function settleHeight(el: HTMLElement, from: number) {
  if (reducedMotion() || el.offsetHeight === from) return;
  gsap.fromTo(
    el,
    { height: from, overflow: "hidden" },
    { height: "auto", duration: 0.3, ease: "power2.out", overwrite: true, clearProps: "height,overflow" }
  );
}

/**
 * Opens or closes a native <details>, animating its last child (the body; keep
 * padding inside it so it folds to 0). Returns true when opening.
 */
export function toggleDetails(details: HTMLDetailsElement, onDone?: () => void): boolean {
  const opening = !details.open;
  // Open first so the body has a height to grow to; close only once it has folded.
  if (opening) details.open = true;
  animateHeight(details.lastElementChild!, opening, {
    onComplete: () => {
      if (!opening) details.open = false;
      onDone?.();
    },
  });
  return opening;
}

/**
 * Elements still below the fold fade and rise in as they scroll into view
 * (those already on screen are left alone). Returns a cleanup function.
 */
export function revealOnScroll(targets: Element[]): () => void {
  if (!import.meta.client || reducedMotion()) return () => {};
  const below = targets.filter((el) => el.getBoundingClientRect().top > window.innerHeight);
  gsap.set(below, { autoAlpha: 0, y: 24 });
  const observer = new IntersectionObserver(
    (entries) => {
      const shown = entries.filter((entry) => entry.isIntersecting).map((entry) => entry.target);
      if (!shown.length) return;
      shown.forEach((el) => observer.unobserve(el));
      gsap.to(shown, { autoAlpha: 1, y: 0, duration: 0.5, ease: "power2.out", stagger: 0.08, clearProps: "opacity,visibility,transform" });
    },
    { rootMargin: "0px 0px -10% 0px" }
  );
  below.forEach((el) => observer.observe(el));
  return () => observer.disconnect();
}

/** Fades elements in while they rise, one after another. */
export function riseIn(targets: gsap.TweenTarget | null | undefined, { y = 16, stagger = 0.06 } = {}) {
  if (!targets || reducedMotion()) return;
  gsap.from(targets, { autoAlpha: 0, y, duration: 0.4, ease: "power2.out", stagger, clearProps: "opacity,visibility,transform" });
}

// Phones and tablets (below Tailwind's lg): content opens below the fold there.
export const isCompactViewport = (): boolean =>
  import.meta.client && window.matchMedia("(max-width: 1023px)").matches;

// Clears the sticky Navbar (68px) with a little room.
export const NAVBAR_OFFSET = 80;

/** Scrolls so `el` sits just under the sticky Navbar. */
export function scrollUnderNavbar(el: Element) {
  const top = el.getBoundingClientRect().top + window.scrollY - NAVBAR_OFFSET;
  window.scrollTo({ top: Math.max(0, top), behavior: reducedMotion() ? "auto" : "smooth" });
}
